import { useMemo } from 'react';

import {
	createProductSpecificAnalyticsIntegrationPayload,
	ErrorType,
	Severity,
	Product,
	Team,
} from '@atlassian/growth-commons-error-analytics';
import { getTrialDaysLeftFromTrialEndDate } from '@atlassian/upgrades-and-purchases/get-trial-days-left';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { getAGGClient } from '@confluence/graphql/entry-points/getAGGClient.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { ConfluenceEdition } from '@confluence/change-edition/entry-points/ConfluenceEdition.ts';

import { PremiumTrialEotQuery } from './PremiumTrialEotQuery.ts';
import type {
	PremiumTrialEotQuery$data,
	PremiumTrialEotQuery$variables,
} from './__generated__/PremiumTrialEotQuery.graphql.ts';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

export const useIsEligibleForPremiumTrialEot = () => {
	const { edition, isLoggedIn, cloudId } = useSessionData();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const skip = !isLoggedIn || !cloudId || edition !== ConfluenceEdition.PREMIUM;

	const { data, error, loading } = useQueryInterop<
		PremiumTrialEotQuery$data,
		PremiumTrialEotQuery$variables
	>(PremiumTrialEotQuery, {
		skip,
		fetchPolicy: 'cache-first',
		client: getAGGClient(),
		onError: (error: Error) => {
			const eventPayload = createProductSpecificAnalyticsIntegrationPayload({
				product: Product.CONFLUENCE,
				team: Team.GROWTH_LUNA,
				experimentMeta: {
					experimentKey: 'twcg-530_confluence_ai_in_editor_eot',
				},
				errorType: ErrorType.GRAPHQL,
				errorMessage: error?.message,
				severity: Severity.MEDIUM,
				source: 'useIsEligibleForPremiumTrialEot',
				additionalAttributes: {
					cloudId,
				},
			});

			createAnalyticsEvent(eventPayload).fire();
		},
	});

	const isPremiumEdition = edition === ConfluenceEdition.PREMIUM;

	const trialEndDate = data?.license?.trialEndDate;
	const trialDaysLeft = getTrialDaysLeftFromTrialEndDate(trialEndDate);

	const hasCCPBillingSystem = data?.license?.billingSourceSystem === 'CCP';
	const isInTrial = Boolean(trialDaysLeft && trialDaysLeft > 0);
	const hasReached5DaysRemaining = Boolean(
		trialDaysLeft && trialDaysLeft >= 0 && trialDaysLeft <= 5,
	);

	const hasEligibleTrial = useMemo(() => {
		return (
			isPremiumEdition &&
			isLoggedIn &&
			Boolean(cloudId) &&
			hasCCPBillingSystem &&
			isInTrial &&
			hasReached5DaysRemaining
		);
	}, [
		isPremiumEdition,
		isLoggedIn,
		cloudId,
		hasCCPBillingSystem,
		isInTrial,
		hasReached5DaysRemaining,
	]);

	return {
		hasEligibleTrial,
		loading,
		error: error?.message || null,
		trialEndDate,
		trialDaysLeft,
	};
};
